/**
 * mdc.ts
 *
 * MDC (Markdown + YAML front-matter) assembly, schema definitions, and parsing.
 * Zod schemas are the source of truth — TypeScript types are derived from them.
 */

import { z } from "zod";
import { load as yamlLoad } from "js-yaml";
import { TagType } from "./tags-schemas";
import { schemas as messageSchemas } from "./message-structure-schemas";
import { StorageBucketKeySchema } from "./common";

// ─── Regexes ──────────────────────────────────────────────────────────────────

const FRONT_MATTER_RE = /^---\n([\s\S]*?)\n---\n\n?([\s\S]*)$/;

// ─── Schemas ──────────────────────────────────────────────────────────────────

// Structural mirror of proto Reference — avoids importing generated enum syntax
// incompatible with erasableSyntaxOnly. Structurally compatible via TS typing.
export const ReferenceSchema = z.object({
  kind: z.number().int(),
  id: z.string().min(1).max(512),
  ext: z.string().max(32),
  type: z.string().max(128),
  version: z.string().max(128),
  tenant: z.string().max(64),
  title: z.string().max(512),
  description: z.string().max(1024),
  uri: z.string().min(1).max(2048),
  psrefUri: z.string().max(2048),
  params: z.record(z.string(), z.string()),
});

export const UploadBucketSchema = StorageBucketKeySchema.extract(["messages", "attachments"]);

export const MdcUploadReferenceSchema = z.object({
  source: z.literal("upload"),
  position: z.number().int(),
  reference: ReferenceSchema,
  filename: z.string().min(1),
  timestamp: z.number().int(),
  bucket: UploadBucketSchema,
});

export const MdcInlineReferenceSchema = z.object({
  source: z.literal("inline"),
  position: z.number().int(),
  reference: ReferenceSchema,
  kindSlug: TagType,
});

export const MdcReferenceSchema = z.discriminatedUnion("source", [
  MdcUploadReferenceSchema,
  MdcInlineReferenceSchema,
]);

export const MdcParticipantSchema = z.object({
  id: z.string().min(1),
  role: messageSchemas.MessageRole,
});

export const MdcHeaderSchema = z.object({
  messageId: z.string().min(1),
  sessionId: z.string().min(1),
  references: z.array(MdcReferenceSchema),
  participant: MdcParticipantSchema,
});

// ─── Inferred types ───────────────────────────────────────────────────────────

export type ReferenceKindSlug = z.infer<typeof TagType>;
export type Reference = z.infer<typeof ReferenceSchema>;
export type UploadBucket = z.infer<typeof UploadBucketSchema>;
export type MdcUploadReference = z.infer<typeof MdcUploadReferenceSchema>;
export type MdcInlineReference = z.infer<typeof MdcInlineReferenceSchema>;
export type MdcReference = z.infer<typeof MdcReferenceSchema>;
export type MdcParticipant = z.infer<typeof MdcParticipantSchema>;
export type MdcHeader = z.infer<typeof MdcHeaderSchema>;

// ─── Assembly ─────────────────────────────────────────────────────────────────

/**
 * Serialise a typed MdcHeader + markdown body into .mdc format.
 * String values written as plain scalars; all others JSON-serialised.
 * Throws if header or body are absent.
 */
export function assembleMdc(header: MdcHeader, body: string): string {
  if (!header) throw new Error("mdc: header is required");
  if (body === undefined || body === null)
    throw new Error("mdc: body is required");
  const lines = ["---"];
  for (const [key, value] of Object.entries(header)) {
    lines.push(
      `${key}: ${typeof value === "string" ? value : JSON.stringify(value)}`,
    );
  }
  lines.push("---", "", body);
  return lines.join("\n");
}

// ─── Parsing ──────────────────────────────────────────────────────────────────

/**
 * Parse a .mdc string into its typed MdcHeader and markdown body.
 * Throws on missing, malformed, or schema-invalid front-matter — fail-fast.
 */
export function parseMdcHeader(mdc: string): {
  header: MdcHeader;
  body: string;
} {
  const match = FRONT_MATTER_RE.exec(mdc);
  if (!match) throw new Error("mdc: missing or malformed YAML front-matter");

  const yamlBlock = match[1]!;
  const body = match[2]!;

  let raw: unknown;
  try {
    raw = yamlLoad(yamlBlock);
  } catch {
    throw new Error("mdc: malformed YAML front-matter");
  }

  const header = MdcHeaderSchema.parse(raw);

  return { header, body };
}

/**
 * Extract the markdown body from a .mdc string, stripping the YAML front-matter.
 * Throws if front-matter is missing or malformed — fail-fast.
 */
export function parseMdcBody(mdc: string): string {
  const match = FRONT_MATTER_RE.exec(mdc);
  if (!match) throw new Error("mdc: missing or malformed YAML front-matter");
  return match[2]!;
}
